import { DialogTitle } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { QUERY_KEYS } from 'constants/constants'
import {
  useNotificationContext,
  usePartnerContext,
  usePaymentContext
} from '../../context'
import {
  ARCHIVED_CAREER_ACTIONS,
  ARCHIVED_CAREER_HINTS,
  ARCHIVED_CAREER_SKIPPED,
  partitionSpacesByArchivedCareer
} from '../../features/pricing/archived-careers'
import { mutation, query } from '../../graphql'
import { withTalentStatusMeta } from '../../helpers/talent-statuses'
import { useArchivedCareerIds } from '../../hooks/use-archived-career-ids'
import { useCreateLog } from '../../hooks/use-create-log'
import Button from '../tailwind/Button'
import InfoCallout from '../tailwind/InfoCallout'
import Modal from '../tailwind/Modal'
import ArchivedCareerRestoreAction from './archived-career-restore-action'

const FullReset = ({ spaces, updateHandler, open, setOpen }) => {
  const { t } = useTranslation()
  const { is_trial } = usePaymentContext()
  const { success, error } = useNotificationContext()
  const { partner } = usePartnerContext()

  const queryClient = useQueryClient()
  const { createSpaceHistoryLog } = useCreateLog()

  const [isLoading, setIsLoading] = useState(false)

  // D16, jeder Modus. Die Aktion ist bei archivierter Stelle nicht verfügbar
  // (use-space-actions). Wird der Dialog trotzdem damit geöffnet, werden diese
  // Talente übersprungen.
  const { archivedCareerIds } = useArchivedCareerIds({ spaces })
  const { allowed, skipped } = partitionSpacesByArchivedCareer(
    spaces,
    archivedCareerIds
  )
  const onlyArchived = !!skipped.length && !allowed.length

  const fullResetSpace = () => {
    if (onlyArchived) return

    setIsLoading(true)

    const resetSpace = async ({ id: space_id, career_id }) => {
      // Frisch laden, damit der gesetzte Talent-Status aus dem aktuellen Meta
      // entfernt wird und keine veraltete Listen-Kopie zurückgeschrieben wird.
      const fresh = await query({
        query: 'getSpace',
        variables: { id: space_id }
      })

      await mutation({
        mutation: 'updateSpace',
        input: {
          id: space_id,
          owner: null, // Leerer String statt null
          progress: 0,
          customer_dashboard_meta: withTalentStatusMeta(
            fresh?.customer_dashboard_meta,
            null
          ),
          updatedAt: new Date().toISOString()
        }
      })

      // Erst nach dem Reset, damit der Verlauf keinen Reset zeigt, der nicht
      // stattgefunden hat. FULL_RESET ist kein Bewerbungsstatus und lässt
      // application_status unberührt. Ein fehlender Eintrag darf den schon
      // erfolgten Reset nicht als Fehler melden.
      try {
        await createSpaceHistoryLog({
          space_id,
          career_id,
          context: 'SYSTEM',
          status: 'FULL_RESET'
        })
      } catch (err) {
        console.error(err)
      }
    }

    Promise.all(allowed.map(resetSpace))
      .then(() => {
        if (is_trial) {
          queryClient.refetchQueries({
            queryKey: QUERY_KEYS.trialDataByPartner(partner.id),
            type: 'active'
          })
        }

        updateHandler()

        success(
          [
            t('space_actions.full_reset_success'),
            skipped.length &&
              t(ARCHIVED_CAREER_SKIPPED, { count: skipped.length })
          ]
            .filter(Boolean)
            .join(' ')
        )
        setIsLoading(false)
        setOpen(false)
      })
      .catch((err) => {
        error()
        console.error(err)
        setIsLoading(false)
      })
  }

  return (
    <Modal open={open} setOpen={() => setOpen(false)} size='lg'>
      <DialogTitle className='text-center text-2xl font-medium leading-6 text-gray-900'>
        {t('space_actions.modal_full_reset_title')}
      </DialogTitle>
      {onlyArchived ? (
        <InfoCallout
          tone='warning'
          className='mt-4'
          action={
            <ArchivedCareerRestoreAction onNavigate={() => setOpen(false)} />
          }
        >
          {t(ARCHIVED_CAREER_HINTS[ARCHIVED_CAREER_ACTIONS.FULL_RESET])}
        </InfoCallout>
      ) : (
        <span className='mt-2 block text-center text-base text-gray-600'>
          {t('space_actions.modal_full_reset_description')}
        </span>
      )}
      <div className='mt-8 flex flex-row gap-3'>
        <Button.SecondaryXL
          fullWidth
          text={t('space_actions.modal_full_reset_cancel_action')}
          onClick={() => setOpen(false)}
        />
        <Button.PrimaryXL
          fullWidth
          text={
            spaces.length > 1
              ? t('space_actions.modal_full_reset_submit_action_multiple', {
                  count: spaces.length
                })
              : t('space_actions.modal_full_reset_submit_action_single')
          }
          onClick={fullResetSpace}
          isLoading={isLoading}
          disabled={onlyArchived}
          className='bg-red-600 hover:bg-red-500'
        />
      </div>
    </Modal>
  )
}

FullReset.propTypes = {
  spaces: PropTypes.array.isRequired,
  updateHandler: PropTypes.func,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired
}

FullReset.defaultProps = {
  updateHandler: () => undefined
}

export default FullReset
